// src/Pages/Enroll/EnrollPage.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../Firebase/firebaseConfig';
import { doc, getDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import './EnrollPage.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Spinner, Alert } from 'react-bootstrap';

const EnrollPage = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [programName, setProgramName] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch program name
  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const collectionName = type === 'course' ? 'courses' : 'diplomas';
        const docRef = doc(db, collectionName, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProgramName(
            (type === 'course' && data.title?.[i18n.language]) ||
              data.title?.['en'] ||
              (type === 'diploma' && data.name?.[i18n.language]) ||
              data.name?.['en'] ||
              t('Unnamed Program')
          );
        } else {
          setMessage(t('invalidProgramID'));
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (error) {
        console.error('Error fetching program details:', error);
        setMessage(t('failedToFetchProgramDetails'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgramDetails();
  }, [type, id, navigate, i18n.language, t]);

  // Form change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Form submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setSuccessMessage('');

    try {
      if (!formData.name || !formData.email || !formData.phone) {
        setMessage(t('pleaseFillAllFields'));
        setIsSubmitting(false);
        return;
      }

      const enrollmentData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        programType: type,
        programId: id,
        programName,
        timestamp: Timestamp.now(),
      };

      const enrollmentCollection = collection(db, 'enrollments');
      await addDoc(enrollmentCollection, enrollmentData);

      setSuccessMessage(
        `${t('thankYou')} ${formData.name}! ${t('weWillContactYouSoon')}`
      );
      setFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error during enrollment:', error);
      setMessage(t('failedToEnroll'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeLabel = type === 'course' ? t('course', 'Course') : t('diploma', 'Diploma');
  const heading = programName || typeLabel;

  return (
    <main className="enroll-page-container">
      <div className="enroll-card">
        {isLoading ? (
          <div className="spinner-container">
            <Spinner animation="border" variant="primary" />
            <p>{t('loading')}</p>
          </div>
        ) : (
          <>
            <header className="enroll-head">
              <span className="enroll-head__eyebrow">{typeLabel}</span>
              <h1 className="enroll-title">{`${t('Enroll In')} ${heading}`}</h1>
            </header>

            <div className="enroll-body">
              {message && <Alert variant="danger" className="alert-message">{message}</Alert>}

              {successMessage ? (
                <div className="enroll-success">
                  <span className="enroll-success__icon"><i className="fas fa-check"></i></span>
                  <p>{successMessage}</p>
                  <button className="enroll-back" onClick={() => navigate('/Courseslist')}>
                    {t('exploreOurPrograms')} <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="enroll-form">
                  <div className="form-group">
                    <label htmlFor="name">{t('name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('Enter Your Name')}
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t('email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('enterYourEmail')}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">{t('phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('enterYourPhone')}
                      autoComplete="tel"
                      required
                    />
                  </div>
                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? t('submitting') : t('submitEnrollment')}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default EnrollPage;
